import type { CatalogItem } from '@viva/core';
import type { PlayerStatus } from '@viva/player';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCatalog } from '../../state/catalog-context';
import type { PlaybackRequest } from '../../state/playback-context';
import { displayName, isPlayable } from '../../state/playback-context';
import { Scrubber } from '../Scrubber';
import { VolumeControl } from '../VolumeControl';
import { EndCard, ErrorCard } from './PlayerCards';
import { PlayerStats } from './PlayerStats';
import { TrackMenu } from './TrackMenu';
import { qualityName, readQualityPreference, saveQualityPreference, withQuality } from './quality';
import type { Watching } from '../../account/api';
import { useOptionalAccount } from '../../account/AccountGate';
import { markPlaybackOpen } from '../../app-version';
import { listAcross } from '../../category-pages';
import type { LeaseState } from './usePlaybackLease';
import { usePlaybackLease } from './usePlaybackLease';
import { saveSubtitlePreference } from './subtitles';
import type { PictureHealth } from './media-element';
import { healthSample, pictureHealth } from './media-element';
import { usePlaybackSession } from './usePlaybackSession';
import { MatchQuestion } from './MatchQuestion';
import { useVolume } from './useVolume';
import { useWatchProgress } from './useWatchProgress';
import { findNonUhdVersion } from './uhd-fallback';
import { blockedOnDevice, uhdReason } from '../../uhd-access';
import { FavoriteButton } from '../ItemViews';
import { t, useLanguage } from '../../i18n';
import { Icon } from '../Icon';

/** Back keys: Escape/Backspace on keyboards, 461 on webOS, 10009 on Tizen. */
const BACK_KEYS = new Set(['Escape', 'Backspace', 'GoBack', 'BrowserBack']);
const BACK_KEY_CODES = new Set([461, 10009]);
const CONTROLS_HIDE_MS = 4000;
/** Short stalls are normal on live TV; only longer ones are announced. */
const BUFFERING_NOTICE_MS = 1000;
const SKIP_SECONDS = 10;
/** What the admin's "Watching now" shows for this screen. */
function watchStatusOf(state: PlayerStatus['state'], lease: LeaseState['kind'], unsupported: boolean, health: PictureHealth): Watching['status'] {
  if (lease === 'queued' || lease === 'starting') return 'waiting';
  if (unsupported || state === 'error') return 'error';
  if (state === 'playing') return health === 'ok' ? 'playing' : health === 'stalled' ? 'buffering' : health;
  if (state === 'paused' || state === 'buffering' || state === 'ended') return state;
  return 'loading';
}

/** How often the picture and sound are checked while playing. */
const HEALTH_CHECK_MS = 4000;

/** Behind live by at least this much (after a pause) shows "Go live" instead of the LIVE badge. */
const BEHIND_LIVE_MIN_S = 3;

/** "2:35", "1:02:10" */
function formatBehind(seconds: number): string {
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  const m = Math.floor(seconds / 60) % 60;
  const h = Math.floor(seconds / 3600);
  return h > 0 ? `${h}:${m.toString().padStart(2, '0')}:${s}` : `${m}:${s}`;
}

function statusText(status: PlayerStatus, bufferingNoticed: boolean, probing: boolean): string | null {
  // Viewers only see plain words; technical details stay in the Info panel.
  if (probing) return t('common.loading');
  switch (status.state) {
    case 'loading':
      return t('common.loading');
    case 'buffering':
      return bufferingNoticed ? t('player.buffering') : null;
    case 'reconnecting':
      return status.dropped ? t('player.reconnecting') : t('common.loading');
    case 'switching':
      return t('common.loading');
    case 'paused':
      return status.byOtherApp ? t('player.pausedByOtherApp') : t('player.paused');
    case 'ended':
      return t('player.finished');
    default:
      return null;
  }
}

export function PlayerOverlay({ request, onClose }: { request: PlaybackRequest; onClose: () => void }) {
  useLanguage();
  const { catalog } = useCatalog();
  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(request);
  // Live channels listed once for all their qualities (#100): the chosen one is kept for the next channels.
  const [quality, setQuality] = useState(readQualityPreference);
  // Qualities this device couldn't play (4K is often HEVC, which most browsers can't decode).
  const [unplayable, setUnplayable] = useState<ReadonlySet<string>>(() => new Set());
  const playing = useMemo(
    () => withQuality(current.item, quality !== null && !unplayable.has(quality) ? quality : null),
    [current.item, quality, unplayable],
  );
  // The lease needs the player's state and the player needs the lease: the state from the previous render is used.
  const [watchStatus, setWatchStatus] = useState<Watching['status']>('loading');
  // An app update waits while the player is open.
  useEffect(() => {
    markPlaybackOpen(true);
    return () => markPlaybackOpen(false);
  }, []);
  const subscriber = useOptionalAccount()?.account.kind === 'paid';
  const lease = usePlaybackLease({
    title: displayName(current.item),
    kind: current.item.kind,
    status: watchStatus,
    stream: playing.kind === 'live' ? (playing.url ?? undefined) : undefined,
  });
  // 4K/8K this device can't play, or 4K stopped on the relay (#301): not opened, the HD version is looked for below.
  const blockedUhd = blockedOnDevice(playing, current.series?.name);
  const session = usePlaybackSession(videoRef, playing, lease.ready, blockedUhd);
  const { status, tracks, conversion } = session;
  // While the player says "playing", check what really reaches the viewer (picture moving, sound decoded).
  const [health, setHealth] = useState<PictureHealth>('ok');
  useEffect(() => {
    const video = videoRef.current;
    if (!video || status.state !== 'playing') {
      setHealth('ok');
      return undefined;
    }
    let previous = healthSample(video);
    const timer = setInterval(() => {
      const next = healthSample(video);
      setHealth(pictureHealth(video, previous, next));
      previous = next;
    }, HEALTH_CHECK_MS);
    return () => clearInterval(timer);
  }, [status.state, current, playing]);
  // A chosen quality the device can't play: back to the last one that played, with a note.
  const variantsNow = current.item.data.variants;
  const playingQuality = variantsNow?.find((variant) => variant.url === playing.url)?.quality ?? null;
  const lastPlayedQuality = useRef<string | null>(null);
  const [qualityNote, setQualityNote] = useState<string | null>(null);
  useEffect(() => {
    if (status.state === 'playing') lastPlayedQuality.current = playingQuality;
  }, [status.state, playingQuality]);
  const formatRejected = session.unsupportedVideo || (status.state === 'error' && status.error.code === 'unsupported');
  useEffect(() => {
    if (!formatRejected || playingQuality === null || playing.url === current.item.url) return;
    const listed = variantsNow?.find((variant) => variant.url === current.item.url)?.quality ?? null;
    const back = lastPlayedQuality.current !== null && lastPlayedQuality.current !== playingQuality ? lastPlayedQuality.current : listed;
    setUnplayable((known) => new Set(known).add(playingQuality));
    setQuality(back);
    if (back !== null) saveQualityPreference(back);
    setQualityNote(t('player.qualityUnplayable', { quality: qualityName(playingQuality, t('player.qualityStandard')) }));
  }, [formatRejected, playingQuality, playing.url, current.item.url, variantsNow]);
  useEffect(() => {
    if (!qualityNote) return undefined;
    const timer = setTimeout(() => setQualityNote(null), 5000);
    return () => clearTimeout(timer);
  }, [qualityNote]);
  const nextWatchStatus = watchStatusOf(status.state, lease.state.kind, session.unsupportedVideo || session.openFailed, health);
  useEffect(() => setWatchStatus(nextWatchStatus), [nextWatchStatus]);
  const { volume, setVolume, step, toggleMute, toast: volumeToast } = useVolume(videoRef);
  const leaseLost = lease.state.kind === 'replaced' || lease.state.kind === 'ended' || lease.state.kind === 'full';
  // Another screen took this account's stream slot, or the trial hour is over.
  useEffect(() => {
    if (leaseLost) videoRef.current?.pause();
  }, [leaseLost]);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [menu, setMenu] = useState<'audio' | 'text' | 'quality' | null>(null);
  const [bufferingNoticed, setBufferingNoticed] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stalls = useRef(0);

  useEffect(() => setCurrent(request), [request]);

  // A 4K movie or episode this device can't play (#224): open its HD/LQ version, else explain.
  const [uhdLookup, setUhdLookup] = useState<'searching' | 'none' | null>(null);
  // The session clears its verdict one render after a switch: only trust one made for the title playing now.
  const [uhdClearedFor, setUhdClearedFor] = useState<string | null>(null);
  useEffect(() => {
    if (!session.unsupportedVideo) setUhdClearedFor(playing.url);
  }, [session.unsupportedVideo, playing.url]);
  const uhdBlocked = session.unsupportedVideo && playingQuality === null && uhdClearedFor === playing.url;
  // Versions already tried for the title opened, so two unplayable copies never take turns.
  const uhdTried = useRef(new Set<string>());
  useEffect(() => {
    uhdTried.current = new Set();
  }, [request]);
  useEffect(() => {
    if (!uhdBlocked) {
      setUhdLookup(null);
      return undefined;
    }
    let cancelled = false;
    setUhdLookup('searching');
    uhdTried.current.add(current.item.key);
    findNonUhdVersion(catalog, current).then(
      (other) => {
        if (cancelled) return;
        if (!other || uhdTried.current.has(other.item.key)) {
          setUhdLookup('none');
          return;
        }
        setQualityNote(t(uhdReason() === 'relay' ? 'player.uhdPausedSwitched' : 'player.uhdSwitched'));
        setCurrent(other);
      },
      () => {
        if (!cancelled) setUhdLookup('none');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [uhdBlocked, catalog, current]);

  useEffect(() => {
    setMenu(null);
    stalls.current = 0;
  }, [current]);

  useEffect(() => {
    if (status.state !== 'buffering') {
      setBufferingNoticed(false);
      return undefined;
    }
    stalls.current++;
    const timer = setTimeout(() => setBufferingNoticed(true), BUFFERING_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [status.state]);


  const showControls = useCallback(() => {
    setControlsVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
  }, []);

  useEffect(() => {
    showControls();
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [showControls, current]);

  const zap = useCallback(
    async (step: number) => {
      const queue = current.queue;
      if (queue) {
        const index = queue.index + step;
        const item = queue.items[index];
        if (item) setCurrent({ item, series: current.series, queue: { ...queue, index } });
        return;
      }
      const list = current.list;
      if (!list || list.total < 2) return;
      let index = list.index;
      // Skip entries without a URL, at most one full lap.
      for (let tries = 0; tries < Math.min(list.total, 20); tries++) {
        index = (index + step + list.total) % list.total;
        const page = list.categories
          ? await listAcross(catalog, list.sourceId, list.kind, list.categories, index, 1)
          : await catalog.listItems({ sourceId: list.sourceId, kind: list.kind, categoryKey: list.categoryKey, offset: index, limit: 1 });
        const item: CatalogItem | undefined = page.items[0];
        if (isPlayable(item)) {
          setCurrent({ item, list: { ...list, index } });
          return;
        }
      }
    },
    [catalog, current.list, current.queue, current.series],
  );

  const toggleFullscreen = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else if (root.requestFullscreen) void root.requestFullscreen().catch(() => undefined);
    // iPhone Safari only puts the video element itself in fullscreen (the native player).
    else (videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null)?.webkitEnterFullscreen?.();
  }, []);

  const live = current.item.kind === 'live';
  const { seekBy, togglePause } = session;
  useWatchProgress(current, status.state, session.time, session.seekTo);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      showControls();
      const key = event.key;
      if (BACK_KEYS.has(key) || BACK_KEY_CODES.has(event.keyCode)) {
        event.preventDefault();
        if (menu) setMenu(null);
        else onClose();
      } else if (key === 'ArrowUp' || key === 'ChannelUp' || key === 'PageUp') {
        event.preventDefault();
        void zap(-1);
      } else if (key === 'ArrowDown' || key === 'ChannelDown' || key === 'PageDown') {
        event.preventDefault();
        void zap(1);
      } else if (key === ' ' || key === 'MediaPlayPause') {
        event.preventDefault();
        togglePause();
      } else if ((key === 'ArrowLeft' || key === 'MediaRewind') && !live) {
        event.preventDefault();
        seekBy(-SKIP_SECONDS);
      } else if ((key === 'ArrowRight' || key === 'MediaFastForward') && !live) {
        event.preventDefault();
        seekBy(SKIP_SECONDS);
      } else if (key === 'f') {
        toggleFullscreen();
      } else if (key === 'm' || key === 'AudioVolumeMute') {
        toggleMute();
      } else if (key === '+' || key === '=' || key === 'AudioVolumeUp') {
        step(1);
      } else if (key === '-' || key === '_' || key === 'AudioVolumeDown') {
        step(-1);
      } else if (key === 'i' || key === 'Info') {
        setShowStats((visible) => !visible);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menu, onClose, showControls, togglePause, toggleFullscreen, zap, seekBy, live, toggleMute, step]);

  const item = current.item;
  const variants = item.data.variants ?? [];
  const activeQuality = variants.find((variant) => variant.url === playing.url)?.quality ?? '';
  const qualityTracks = variants.filter((variant) => !unplayable.has(variant.quality)).map((variant) => ({
    id: variant.quality,
    label: qualityName(variant.quality, t('player.qualityStandard')),
    active: variant.url === playing.url,
  }));
  const controller = session.controllerRef.current;
  // Live: channel up/down through the catalog, or through a fixed list (a match's tested channels).
  const canStep = live ? !!current.list || !!current.queue : !!current.queue;
  const previousItem = current.queue ? current.queue.items[current.queue.index - 1] : undefined;
  const nextItem = current.queue ? current.queue.items[current.queue.index + 1] : undefined;
  const stepLabel = live ? [`▲ ${t('player.prev')}`, `▼ ${t('player.next')}`] : [`⏮ ${t('player.previous')}`, `${t('player.next')} ⏭`];
  const text = statusText(status, bufferingNoticed, session.probing || uhdLookup === 'searching');
  const activeText = tracks.text.find((track) => track.active);
  const firstFrameMs = session.firstFrameMs;

  return (
    <div ref={rootRef} className="player" data-own-keys data-controls={controlsVisible || status.state !== 'playing'} onMouseMove={showControls} onClick={showControls}>
      <video ref={videoRef} className="player__video" playsInline />
      {status.state === 'error' && !session.unsupportedVideo ? (
        <ErrorCard
          item={item}
          error={status.error}
          live={live}
          onRetry={() => controller?.retry()}
          onNext={(live && current.list) || nextItem ? () => void zap(1) : undefined}
          onBack={onClose}
        />
      ) : null}
      {/* The relay could open nothing at this movie's address (#190): say so now, Retry asks again. */}
      {session.openFailed && status.state !== 'error' ? (
        <ErrorCard
          item={item}
          error={{ code: 'no-start', detail: 'probe failed' }}
          live={live}
          onRetry={session.retryOpen}
          onNext={nextItem ? () => void zap(1) : undefined}
          onBack={onClose}
        />
      ) : null}
      {lease.state.kind === 'queued' || lease.state.kind === 'starting' ? (
        <div className="player__error" role="status">
          {lease.state.kind === 'queued' ? (
            <>
              <h2>{t('player.queueTitle')}</h2>
              <TrialCountdown startsAt={lease.state.startsAt} />
              <p className="muted">
                {lease.state.position === 1 ? t('player.queueNext') : t('player.queuePosition', { position: lease.state.position })} {t('player.queueBody')}
              </p>
            </>
          ) : (
            <p className="muted">{t('player.connecting')}</p>
          )}
          <button type="button" className="button" onClick={onClose}>
            {t('common.back')}
          </button>
        </div>
      ) : null}
      {uhdBlocked && uhdLookup === 'none' ? (
        <div className="player__error" role="alert">
          <h2>{t(uhdReason() === 'relay' ? 'player.uhdPausedTitle' : 'player.uhdTitle')}</h2>
          <p className="muted">{t(uhdReason() === 'relay' ? 'player.uhdPausedBody' : 'player.uhdBody')}</p>
          <button type="button" className="button" onClick={onClose}>
            {t('common.back')}
          </button>
        </div>
      ) : null}
      {lease.state.kind === 'ended' ? (
        <div className="player__error" role="alert">
          {subscriber ? (
            <>
              <h2>{t('noAccess.expired.title')}</h2>
              <p className="muted">{t('player.renewBody')}</p>
            </>
          ) : (
            <>
              <h2>{t('noAccess.trialEnded.title')}</h2>
              <p className="muted">{t('player.trialEndedBody')}</p>
            </>
          )}
          <button type="button" className="button" onClick={onClose}>
            {t('common.back')}
          </button>
        </div>
      ) : null}
      {lease.state.kind === 'full' ? (
        <div className="player__error" role="alert">
          <h2>{t(lease.state.maxStreams === 1 ? 'player.fullOne' : 'player.full', { count: lease.state.maxStreams })}</h2>
          <p className="muted">{t(lease.state.maxStreams === 1 ? 'player.fullBodyOne' : 'player.fullBody', { count: lease.state.maxStreams })}</p>
          {lease.state.screens.map((screen) => (
            <button key={screen.leaseId} type="button" className="button primary" onClick={() => lease.takeOver(screen.leaseId)}>
              {t('player.stopAndWatch', { device: screen.title ? `${screen.device} (${screen.title})` : screen.device })}
            </button>
          ))}
          <button type="button" className="button" onClick={onClose}>
            {t('common.back')}
          </button>
        </div>
      ) : null}
      {lease.state.kind === 'replaced' ? (
        <div className="player__error" role="alert">
          <h2>{t('player.replacedTitle')}</h2>
          <p className="muted">{t('player.replacedBody')}</p>
          <button
            type="button"
            className="button primary"
            onClick={() => {
              lease.takeOver();
              void videoRef.current?.play().catch(() => undefined);
            }}
          >
            {t('player.watchHere')}
          </button>
          <button type="button" className="button" onClick={onClose}>
            {t('common.back')}
          </button>
        </div>
      ) : null}
      {text && status.state !== 'error' && lease.ready ? <div className="player__status" role="status">{text}</div> : null}
      {status.state === 'playing' && status.soundOff ? (
        <button type="button" className="player__notice player__unmute" onClick={session.unmute}>
          {/* Phones get the short line at the top edge, so the notice doesn't cover the match (landscape). */}
          <span className="player__unmute-long">{t('player.soundOffByOtherApp')}</span>
          <span className="player__unmute-short">{t('player.soundOffShort')}</span>
        </button>
      ) : null}
      {qualityNote ? (
        <div className="player__toast" role="status">
          {qualityNote}
        </div>
      ) : null}
      {volumeToast ? (
        <div className="player__toast" role="status">
          {volume.muted ? t('player.muted') : t('player.volumePercent', { percent: Math.round(volume.volume * 100) })}
        </div>
      ) : null}
      <MatchQuestion item={current.item} playing={status.state === 'playing'} />
      {showStats ? (
        <PlayerStats videoRef={videoRef} controllerRef={session.controllerRef} stalls={stalls} firstFrameMs={firstFrameMs} conversion={conversion} />
      ) : null}
      <div className="player__top">
        <button type="button" className="button ghost" onClick={onClose} aria-label={t('common.back')}>
          <Icon name="back" />
          {t('common.back')}
        </button>
        <div className="player__title">
          {live && item.data.channelNumber ? <span className="muted">{item.data.channelNumber} </span> : null}
          <strong>{displayName(item)}</strong>
        </div>
        {live ? <FavoriteButton item={current.item} className="player__favorite" /> : null}
      </div>
      {status.state === 'ended' ? (
        <EndCard item={item} nextItem={nextItem} onNext={() => void zap(1)} onReplay={() => session.seekTo(0)} onBack={onClose} />
      ) : null}
      <div className="player__bottom">
        {live ? (
          <div className="player__live">
            {/* Paused live TV plays on from where it was paused; this jumps back to the broadcast. */}
            {session.behindLive >= BEHIND_LIVE_MIN_S ? (
              <button type="button" className="button player__go-live" onClick={session.goLive} aria-label={t('player.behindLive', { time: formatBehind(session.behindLive) })}>
                <span className="player__behind">−{formatBehind(session.behindLive)}</span>
                <span className="player__go-live-dot" aria-hidden="true" />
                {t('player.goLive')}
              </button>
            ) : (
              <span className="badge badge--live">{t('clock.live')}</span>
            )}
          </div>
        ) : (
          <Scrubber position={session.time.position} duration={session.time.duration} bufferedEnd={session.time.bufferedEnd} onSeek={session.seekTo} />
        )}
        <div className="player__buttons">
          {canStep && (live || previousItem) ? (
            <button type="button" className="button" onClick={() => void zap(-1)} aria-label={live ? t('player.previousChannel') : t('player.previousEpisode')}>
              {stepLabel[0]}
            </button>
          ) : null}
          {!live ? (
            <button type="button" className="button" onClick={() => seekBy(-SKIP_SECONDS)} aria-label={t('player.back10')}>
              −10 s
            </button>
          ) : null}
          <button type="button" className="button" onClick={togglePause}>
            {status.state === 'paused' || status.state === 'ended' ? t('player.play') : t('player.pause')}
          </button>
          {!live ? (
            <button type="button" className="button" onClick={() => seekBy(SKIP_SECONDS)} aria-label={t('player.forward10')}>
              +10 s
            </button>
          ) : null}
          {canStep && (live || nextItem) ? (
            <button type="button" className="button" onClick={() => void zap(1)} aria-label={live ? t('player.nextChannel') : t('player.nextEpisode')}>
              {stepLabel[1]}
            </button>
          ) : null}
          <span className="player__spacer" />
          <VolumeControl state={volume} onChange={(next) => setVolume(next)} />
          {variants.length > 1 ? (
            <button type="button" className="button" aria-expanded={menu === 'quality'} onClick={() => setMenu(menu === 'quality' ? null : 'quality')}>
              {t('player.quality')}: {qualityName(activeQuality, t('player.qualityStandard'))}
            </button>
          ) : null}
          {tracks.audio.length > 1 ? (
            <button type="button" className="button" aria-expanded={menu === 'audio'} onClick={() => setMenu(menu === 'audio' ? null : 'audio')}>
              {t('player.audio')}
            </button>
          ) : null}
          {tracks.text.length > 0 ? (
            <button type="button" className="button" aria-expanded={menu === 'text'} onClick={() => setMenu(menu === 'text' ? null : 'text')}>
              {t('player.subtitles')}{activeText ? `: ${activeText.label}` : ''}
            </button>
          ) : null}
          <button type="button" className="button" aria-pressed={showStats} onClick={() => setShowStats((visible) => !visible)}>
            {t('player.info')}
          </button>
          <button type="button" className="button" onClick={toggleFullscreen}>
            {t('player.fullscreen')}
          </button>
        </div>
      </div>
      {menu ? (
        <TrackMenu
          kind={menu}
          tracks={menu === 'audio' ? tracks.audio : menu === 'text' ? tracks.text : qualityTracks}
          onSelect={(id) => {
            if (menu === 'quality' && id !== null) {
              setQuality(id);
              saveQualityPreference(id);
            } else if (menu === 'audio' && id !== null) controller?.selectAudio(id);
            else if (menu === 'text') {
              controller?.selectText(id);
              // Remembered for the next movie or episode ("off" too).
              saveSubtitlePreference(id === null ? null : (tracks.text.find((track) => track.id === id)?.language ?? null));
            }
            setMenu(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** Ticking "12:34" until the expected start; "any moment now" once it is reached (a spot may free up later than estimated). */
function TrialCountdown({ startsAt }: { startsAt: number }) {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const left = Math.max(0, Math.round((startsAt - now) / 1000));
  return (
    <p className="player__countdown" role="timer" aria-live="off">
      {left > 0 ? formatBehind(left) : t('player.anyMoment')}
    </p>
  );
}
